import React, { useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import CourseSelector from "../components/CourseSelector";
import EndorsementModal from "../components/EndorsementModal";
import { baseCourses, endorsementCourses } from "../mock";
import { goToCheckout } from "../lib/checkout";

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="relative pt-[76px] bg-[#F4F7FB] diag-lines">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="font-display text-5xl md:text-6xl text-brand mt-3">{title}</h1>
        {subtitle && (
          <p className="text-slate-600 text-lg mt-4 max-w-2xl">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

function CourseRow({ c, onEnroll }) {
  const Icon = c.icon;
  return (
    <div className="card-lift bg-white border border-slate-200 rounded-2xl p-7 flex flex-col md:flex-row md:items-center gap-6 soft-shadow">
      <div className="flex items-start gap-5 flex-1">
        <div className="w-14 h-14 shrink-0 rounded-xl bg-[#16365C]/8 flex items-center justify-center">
          <Icon className="w-7 h-7 text-steel" />
        </div>
        <div>
          <h3 className="font-head font-semibold text-slate-900 text-xl">{c.name}</h3>
          <p className="text-steel text-xs uppercase tracking-wide mt-0.5 font-semibold">
            {c.tagline}
          </p>
          <p className="text-slate-500 text-sm mt-3 leading-relaxed max-w-xl">
            {c.description}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-5 md:flex-col md:items-end">
        <span className="font-display text-3xl text-brand">${c.price}</span>
        <button
          onClick={() => onEnroll(c.id)}
          className="btn-brand rounded-md px-6 py-3 text-sm whitespace-nowrap inline-flex items-center gap-2"
        >
          Enroll <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const includedItems = [
  "100% online — no classroom required",
  "All FMCSA-required theory topics",
  "Included practice test",
  "Works on any device — phone, tablet, laptop",
  "Results reported to the National Registry",
  "Self-paced learning with in-house support",
];

export default function Courses() {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeCourse, setActiveCourse] = useState(null);

  const openModal = (id) => {
    setActiveCourse(id);
    setModalOpen(true);
  };

  return (
    <main>
      <PageHero
        eyebrow="Our Programs"
        title="Courses & Pricing"
        subtitle="Every class is 100% online — no classroom required. CDL license classes are $119.95 and endorsements just $59.95, each covering everything the FMCSA requires. Choose a class, add endorsements, and get on the road."
      />

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-head font-semibold uppercase tracking-wider text-slate-500 text-sm mb-6">
            License Classes
          </h2>
          <div className="space-y-5">
            {baseCourses.map((c) => (
              <CourseRow key={c.id} c={c} onEnroll={openModal} />
            ))}
          </div>

          <h2 className="font-head font-semibold uppercase tracking-wider text-slate-500 text-sm mb-6 mt-14">
            Endorsements
          </h2>
          <div className="space-y-5">
            {endorsementCourses.map((c) => (
              <CourseRow key={c.id} c={c} onEnroll={(id) => goToCheckout([id])} />
            ))}
          </div>

          {/* What's included */}
          <div className="mt-16 bg-[#F4F7FB] border border-slate-200 rounded-2xl p-8">
            <h3 className="font-display text-3xl text-brand mb-6">
              What&apos;s <span className="text-steel">Included</span>
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {includedItems.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CourseSelector />
      <EndorsementModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        baseCourseId={activeCourse}
      />
    </main>
  );
}
